import { alpha, Box, CircularProgress, Divider, SxProps, Tooltip } from '@mui/material';
import React from 'react';
import { IContentLibVideoProps } from './type';
import Stack from '@mui/material/Stack';
import { Text } from '../../../../../common/components/Text/Text';
import { theme } from '../../../../../common/styles/theme';
import { ButtonMenu } from '../../../../../common/components/ButtonMenu/ButtonMenu';
import { ConfirmPopup } from '../../../ConfirmPopup/ConfirmPopup';
import { IconMenuItem } from '../../../../../common/components/IconMenuItem/IconMenuItem';
import { TrashIcon } from '../../../../../../icons/TrashIcon';
import { AudioIcon } from '../../../../../../icons/AudioIcon';
import { CloseIcon } from '../../../../../../icons/CloseIcon';
import { LibraryIcon } from '../../../../../../icons/LibraryIcon';
import { LangLevelUtils } from '../../../../../common/utils/langLevelUtils';
import { PlayListLevelTitles } from './playListLevelTitles';
import { EUserLevel } from '../../../../store/general/types';
import { Level1Icon } from '../../../../../../icons/Level1Icon';
import { Level2Icon } from '../../../../../../icons/Level2Icon';
import { LevelsIcon } from '../../../../../../icons/LevelsIcon';
import { PencilIcon } from '../../../../../../icons/PencilIcon';
import { ShareIcon } from '../../../../../../icons/ShareIcon';
import { DisketteIcon } from '../../../../../../icons/DisketteIcon';
import { TextIcon } from '../../../../../../icons/TextIcon';
import { ListIcon } from '../../../../../../icons/ListIcon';
import { ChannelIcon } from '../../../../../../icons/ChannelIcon';
import { CheckIcon } from '../../../../../../icons/CheckIcon';
import { SchoolIcon } from '../../../../../../icons/SchoolIcon';
import { TimeUtils } from '../../../../../common/utils/time-utils';
import { isProgressLogActive } from '../../../../../common/utils/conferenceProgress';
import { ArrowCircleIcon } from '../../../../../../icons/ArrowCircleIcon';
import { SettingsIcon } from '../../../../../../icons/SettingsIcon';
import { TConferenceSettingsSection } from '../../../call/ConferenceSettings/components/types';
import { CallRecordingProgressModalHOC } from '../../../call/CallRecordingProgressModal/hocs/CallRecordingProgressModalHOC';
import { NoteIndicatorIcon } from '../../../../../../icons/NoteIndicatorIcon';
import { EndlessBookIcon } from '../../../../../../icons/EndlessBookIcon';
import { LARGE_TOOLTIP_SLOT_PROPS } from '../../../../../common/styles/tooltip';
import { TextCaptionsIcon } from '../../../../../../icons/TextCaptionsIcon';
import { TextCodesIcon } from '../../../../../../icons/TextCodesIcon';
import { useIsMobileLayout } from '../../../../../common/hooks/useIsMobileLayout';

// Only one progress modal may be open across all tiles at once.
// The owner tile id holds the shared slot; listeners let every other tile close itself when the owner changes.
let logModalOwner: number | null = null;
let logModalIdCounter = 0;
const logModalListeners = new Set<() => void>();
const notifyLogModal = () => logModalListeners.forEach((l) => l());

// The tile remounts on every log update, so a local "closed" flag would be lost and the
// modal would reopen on the next line. Persist the dismissal outside the component, keyed
// by recording + run, so it survives remounts; a new run gets a fresh key and reopens.
const logModalDismissed = new Set<string>();

export function ContentLibVideo({
	title,
	titlePrefix,
	titleTooltip,
	modalTitle,
	thumbUrl,
	channel,
	channelUrl,
	isAudio,
	isText,
	isRecording,
	recordingThumb,
	isCall,
	isBig,
	isSaved,
	level,
	disableClick,
	isActive,
	canEdit,
	watchPercent,
	groupTitle,
	duration,
	isProcessing: _isProcessing,
	audioId,
	logKey,
	progressLog = [],
	startedAt,
	stepStartedAt,
	updatedAt,
	info,
	isCallUnprocessed,
	hasAudio,
	hasVideo,
	saveLevel,
	bookId,
	onClick,
	onDelete,
	onDeleteForEveryone,
	onDeleteAudio,
	onDeleteVideo,
	onDeleteVideoAndAudio,
	onGetCopyLink,
	onDownload,
	onDownloadCaptions,
	onDownloadCaptionsWithTimecodes,
	onCreateBook,
	onOpenBook,
	onEdit,
	onMove,
	onUpdateGroup,
	onClickChannel,
	onReprocess,
	onProcess,
	onCancel,
	onCloseSettings,
}: IContentLibVideoProps) {

	const className = 'content-lib-video';
	const classControl = className + '__control';
	const isMobileLayout = useIsMobileLayout();

	const [isLongTitle, setLongTitle] = React.useState(false);
	const ref1 = React.useRef(null);
	const ref2 = React.useRef(null);
	const [locked, setLocked] = React.useState(false);
	// Log lives in the store, so it survives this tile remounting. "Active" while
	// its last line isn't terminal (Done / Failed).
	const lastMessage = progressLog[progressLog.length - 1] || '';
	const isLogActive = isProgressLogActive(progressLog);

	// A live store log means processing too, so a refresh (progress restored via
	// /groups) shows progress instead of "Process failed".
	const [isProcessing, setProcessing] = React.useState(_isProcessing || isLogActive);

	React.useEffect(() => {
        setProcessing(_isProcessing || isLogActive);
    }, [_isProcessing, isLogActive]);

	// Dismissal is keyed by recording + run (startedAt). A new run gets a fresh key, so it
	// reopens; within a run the modal stays closed across remounts and new log lines.
	// Never key on title alone: it changes mid-run (AI title, user edit) and isn't unique.
	const logRunKey = `${logKey || audioId || title}:${startedAt ?? ''}`;

	// Auto-open while processing: on mount (the tile may mount late, mid-run) and on
	// each new line. Never auto-open once terminal, never auto-close, never reopen once dismissed.
	const [logOpen, setLogOpen] = React.useState(isLogActive && !logModalDismissed.has(logRunKey));
	// Reprocess opens the modal in "settings" mode (before any run) so the user can adjust
	// conference settings and press Process; it isn't tied to an active progress log.
	// null = closed; otherwise the section to open with (Settings → info, Transcribe again → processing)
	const [settingsSection, setSettingsSection] = React.useState<TConferenceSettingsSection | null>(null);
	const reprocessSettingsOpen = !!settingsSection;
	const prevLogLenRef = React.useRef(progressLog.length);
	const mountedRef = React.useRef(false);
	const logModalId = React.useRef(++logModalIdCounter).current;

	// Take the single shared modal slot, closing whichever tile held it before.
	const claimLogModal = () => {
		logModalOwner = logModalId;
		notifyLogModal();
	};
	const releaseLogModal = () => {
		if (logModalOwner === logModalId) logModalOwner = null;
	};
	// Force this tile's modal open (manual actions): always wins the slot.
	const openLog = () => {
		claimLogModal();
		setLogOpen(true);
	};

	React.useEffect(() => {
		// Close ourselves whenever another tile claims the shared slot.
		const onOwnerChange = () => {
			if (logModalOwner !== logModalId) setLogOpen(false);
		};
		logModalListeners.add(onOwnerChange);
		// Mounted already showing (tile mounted mid-run): claim the slot if it's free,
		// otherwise stand down so only the current owner stays open.
		if (logOpen) {
			if (logModalOwner === null) claimLogModal();
			else setLogOpen(false);
		}
		return () => {
			logModalListeners.delete(onOwnerChange);
			releaseLogModal();
		};
	}, []);

	React.useEffect(() => {
		// Open on each new line while a same-instance run keeps going, unless the user
		// dismissed this run. Don't steal the slot from a modal the user is already looking at.
		const canOpen = logModalOwner === null || logModalOwner === logModalId;
		if (mountedRef.current
			&& progressLog.length > prevLogLenRef.current
			&& !logModalDismissed.has(logRunKey)
			&& canOpen
		) {
			openLog();
		}
		prevLogLenRef.current = progressLog.length;
		mountedRef.current = true;
	}, [progressLog]);

	const prevRunKeyRef = React.useRef(logRunKey);
	React.useEffect(() => {
		// A fresh run in this same tile (e.g. reprocess) gets a new key — reopen it.
		if (prevRunKeyRef.current !== logRunKey) {
			prevRunKeyRef.current = logRunKey;
			const canOpen = logModalOwner === null || logModalOwner === logModalId;
			if (isLogActive && !logModalDismissed.has(logRunKey) && canOpen) openLog();
		}
	}, [logRunKey]);

	const handleLogClose = () => {
		logModalDismissed.add(logRunKey);
		releaseLogModal();
		setLogOpen(false);
		setSettingsSection(null);
		// Apply the deferred conference-settings sync (group move) now that the tile can remount.
		onCloseSettings?.();
	};

	const userLevel = level && LangLevelUtils.getUserLevelByVideoLevel(level);

    React.useEffect(() => {
        if (!isLongTitle && ref1.current && ref2.current) {
			//@ts-ignore
			if (ref2.current.offsetHeight > ref1.current.offsetHeight) {
				setLongTitle(true);
			}
		}
    }, [title]);

	const titleElement = <Text
		className={`${className}__title`}
		lineHeight={`1.3 !important`}
		sx={{
			display: '-webkit-box',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			WebkitLineClamp: 2,
			WebkitBoxOrient: 'vertical',
			maxHeight: '2.6em',

			fontSize: theme.typography.subtitle2,
			fontWeight: 600,
		}}
		ref={ref1}
	>
		<span
			ref={ref2}
		>
			{titlePrefix &&
				<Box
					component={'span'}
					sx={{
						display: 'inline-flex',
						alignItems: 'center',
						height: '1.3em', // one title line, so the flag centers on it
						verticalAlign: 'top',
						mr: .75,

						'& *': {
							lineHeight: 1,
						},
					}}
				>
					{titlePrefix}
				</Box>
			}
			{title}
		</span>
	</Text>

	const menuBtnRef = React.useRef<HTMLButtonElement | null>(null);
	// One shared anchor + a tag for which destructive action is pending, so each shows its own
	// confirm text instead of the fixed "Delete item?" this used to be limited to.
	type TPendingAction = 'delete' | 'deleteAudio' | 'deleteVideo' | null;
	const [pendingAction, setPendingAction] = React.useState<TPendingAction>(null);
	const [menuOpen, setMenuOpen] = React.useState(false);

	const handleMenuOpen = () => {
		setMenuOpen(true);
	}
	const handleMenuClose = () => {
		setMenuOpen(false);
	}
	const handleConfirmDelete = () => {
		handleMenuClose();
		setPendingAction('delete');
	}
	const handleConfirmDeleteAudio = () => {
		handleMenuClose();
		setPendingAction('deleteAudio');
	}
	const handleConfirmDeleteVideo = () => {
		handleMenuClose();
		setPendingAction('deleteVideo');
	}
	// 'delete' normally only removes it from this library; the checkbox (Telegram-style) escalates
	// to wiping it for every participant. 'deleteVideo' normally keeps the standalone audio file;
	// the radio picks whether to drop that too.
	const handleConfirmedAction = (selection: { option?: string; checked?: boolean }) => {
		const action = pendingAction;
		setPendingAction(null);
		if (action === 'delete') {
			if (selection.checked && onDeleteForEveryone) onDeleteForEveryone();
			else onDelete?.();
		} else if (action === 'deleteAudio') {
			onDeleteAudio?.();
		} else if (action === 'deleteVideo') {
			if (selection.option === 'videoAndAudio' && onDeleteVideoAndAudio) onDeleteVideoAndAudio();
			else onDeleteVideo?.();
		}
	}
	const confirmTextByAction: Record<Exclude<TPendingAction, null>, { text: string; description?: string }> = {
		delete: {
			text: 'Delete this recording?',
		},
		deleteAudio: {
			text: 'Remove audio for everyone?',
			description: 'The transcript can no longer be regenerated once the audio is removed.'
		},
		deleteVideo: {
			text: 'Remove video?',
		},
	};
	const DELETE_VIDEO_OPTIONS = [
		{ value: 'video', label: 'Remove video', hint: 'The video track is removed for everyone — the audio track stays.' },
		{ value: 'videoAndAudio', label: 'Remove video & audio', hint: 'Both tracks are removed for everyone — only the transcript remains.' },
	];
	// Both the menu label and the confirm's radio promise a choice between video-only and
	// video+audio — only true when there's a standalone audio file AND a handler for dropping it too.
	const canChooseVideoAndAudio = hasAudio && !!onDeleteVideoAndAudio;
	const handeGetCopyLink = () => {
		handleMenuClose();
		!!onGetCopyLink && onGetCopyLink();
	}
	const handeMove = () => {
		handleMenuClose();
		!!onMove && onMove();
	}
	const handeUpdateGroup = () => {
		handleMenuClose();
		!!onUpdateGroup && onUpdateGroup();
	}
	const handleCreateBook = () => {
		handleMenuClose();
		!!onCreateBook && onCreateBook();
	}
	const handleOpenBook = () => {
		handleMenuClose();
		!!onOpenBook && onOpenBook();
	}
	const handleShowLog = () => {
		handleMenuClose();
		openLog();
	}

	const handeClick = async (e: React.MouseEvent) => {
		if (isProcessing) return;
		if (locked || disableClick) return;
		const target = e.target as HTMLElement;
		if (target.closest(`.${classControl}`)) return;

		setLocked(true);
		setProcessing(true);
		try {
			await onClick();
		} finally {
			setLocked(false);
			setProcessing(false);
		}
	}

	const runProcessing = async (action?: () => Promise<void>) => {
		if (!action || isProcessing || locked || disableClick) return;

		handleMenuClose();
		setProcessing(true);
		try {
			await action();
		} finally {
			setProcessing(false);
		}
	}

	// Don't reprocess right away: open the modal in settings mode; Process (onReprocess) fires from there.
	const handleOpenReprocessSettings = (section: TConferenceSettingsSection) => {
		handleMenuClose();
		setSettingsSection(section);
		openLog();
	};
	// transcript/nothing keep no media to rebuild from
	const canTranscribeAgain = saveLevel !== 'transcription' && saveLevel !== 'nothing';
	const handeProcess = () => runProcessing(onProcess);

	const levelLabel = !!userLevel &&
		<Tooltip
			title={PlayListLevelTitles[userLevel]}
			placement={'top'}
			disableInteractive
		>
			<Stack
				className={`level${userLevel}`}
				sx={{
					position: 'absolute',
					right: theme.spacing(.5),
					bottom: theme.spacing(.5),
					p: theme.spacing(.25, .5),
					color: theme.palette.grey[100],
					background: alpha(theme.palette.common.white, .9),
					borderRadius: theme.spacing(.5),

					'.MuiSvgIcon-root': {
						width: theme.spacing(1.5),
						height: theme.spacing(1.5),
					},

					'&.level1': {
						color: theme.palette.secondary[200],
					},

					'&.level2': {
						color: theme.palette.secondary[300],
					},

					'&.level3': {
						color: theme.palette.primary.main,
					},
				}}
			>
				{userLevel === EUserLevel.BEGINNER &&
					<Level1Icon />
				}
				{userLevel === EUserLevel.INTERMEDIATE &&
					<Level2Icon />
				}
				{userLevel === EUserLevel.ADVANCED &&
					<LevelsIcon />
				}
			</Stack>
		</Tooltip>

	const durationFormatted = duration ? TimeUtils.secondsToString(duration) : '';

	const isDone = watchPercent !== undefined && watchPercent >=100;
	const percent = watchPercent !== undefined &&
		!isText && !isRecording && (
		<Stack
			className={isDone ? 'done' : ''}
			sx={{
				position: 'absolute',
				left: theme.spacing(1),
				bottom: theme.spacing(1),
				width: theme.spacing(4),
				height: theme.spacing(4),
				justifyContent: 'center',
				alignItems: 'center',
				borderRadius: '50%',
				background: theme.palette.common.white,
				boxShadow: `0px 0px 24px 0px ${theme.palette.grey[400]}`,

				'&.done': {
					color: theme.palette.common.white,
					background: theme.palette.primary.main,
				},
			}}
		>
			<CircularProgress
				variant={'determinate'}
				value={isDone ? 100 : watchPercent}
				size={theme.spacing(5.5)}
				thickness={8}
				sx={{
					position: 'absolute',
					left: theme.spacing(-.75),
					bottom: theme.spacing(-.75),
				}}
			/>
			{isDone
				? <CheckIcon sx={{
					width: theme.spacing(2),
					height: theme.spacing(2),
				}} />
				: <Text
					sx={{
						fontSize: '10px',
						fontWeight: 700,
					}}
				>
					{watchPercent}%
				</Text>
			}
		</Stack>
	);

	const menuPropsSx: SxProps = !!onDownloadCaptions
		? {
			'& > .MuiPaper-root': {
				minWidth: theme.spacing(28),
			},
		}
		: {};

	const menu = (!!onDelete || !!onDeleteAudio || !!onDeleteVideo || !!onGetCopyLink || !!onDownload || !!onDownloadCaptions || !!onCreateBook || (!!bookId && !!onOpenBook) || !!onReprocess || !!onProcess || !!onUpdateGroup || canEdit || channelUrl || !!progressLog.length) && <>
		<ButtonMenu
			buttonProps={{
				className: `${className}__menu ${classControl} ${menuOpen ? 'opened' : ''}`,
				ref: menuBtnRef,
				sx: {
					position: 'absolute',
					right: theme.spacing(-1),
					top: theme.spacing(-1),
					height: theme.spacing(5),
					width: theme.spacing(5),
					background: theme.palette.common.white,
					transform: 'scale(0)',
					pointerEvents: 'none',
					transition: 'all .2s ease',
					boxShadow: theme.shadows[16],

					'&.opened': {
						transform: 'scale(1)',
						pointerEvents: 'all',
					},

					[`.${className}.big &`]: {
						top: theme.spacing(-1.5),
						right: theme.spacing(-2.5),
					},
				},
			}}
			menuProps={{
				className: classControl,
				open: !!menuOpen,
				disablePortal: true,
				sx: menuPropsSx,
			}}
			onOpen={handleMenuOpen}
			onClose={handleMenuClose}
		>
			<>
			{!!progressLog.length &&
				<IconMenuItem
					title={'Show log'}
					onClick={handleShowLog}
				>
					<NoteIndicatorIcon
						sx={{
							width: theme.spacing(1.75) + ' !important',
							height: theme.spacing(1.75) + ' !important',
							color: theme.palette.warning.dark + ' !important',
						}}
					/>
				</IconMenuItem>
			}
			{!!onMove &&
				<IconMenuItem
					title={'Save to my playlist...'}
					onClick={handeMove}
				>
					<DisketteIcon />
				</IconMenuItem>
			}
			{!!onUpdateGroup &&
				<IconMenuItem
					title={'Change playlist'}
					onClick={handeUpdateGroup}
				>
					<ListIcon />
				</IconMenuItem>
			}
			{!!onGetCopyLink &&
				<IconMenuItem
					title={'Share video'}
					onClick={handeGetCopyLink}
				>
					<ShareIcon />
				</IconMenuItem>
			}

			{!!onReprocess && !isProcessing &&
			<IconMenuItem
				title={'Settings'}
				onClick={() => handleOpenReprocessSettings('info')}
			>
				<SettingsIcon />
			</IconMenuItem>
			}

			{!!onReprocess && !isProcessing && canTranscribeAgain &&
			<IconMenuItem
				title={'Transcribe again'}
				onClick={() => handleOpenReprocessSettings('processing')}
			>
				<ArrowCircleIcon />
			</IconMenuItem>
			}

			{!!onProcess && !isProcessing &&
			<IconMenuItem
				title={'Process'}
				onClick={handeProcess}
			>
				<ArrowCircleIcon />
			</IconMenuItem>
			}
			{!!channelUrl && !!onClickChannel &&
				<IconMenuItem
					title={'View channel'}
					onClick={onClickChannel}
				>
					<ChannelIcon />
				</IconMenuItem>
			}

			{!isProcessing && !isCallUnprocessed &&
				<Divider />
			}

			{!!onDownload && !isProcessing && !isCallUnprocessed &&
			<IconMenuItem
				title={isAudio ? 'Download audio' : 'Download'}
				onClick={onDownload}
			>
				<AudioIcon />
			</IconMenuItem>
			}
			{!!onDownloadCaptions && !isProcessing && !isCallUnprocessed &&
			<IconMenuItem
				title={'Download captions'}
				onClick={onDownloadCaptions}
			>
				<TextCaptionsIcon />
			</IconMenuItem>
			}
			{!!onDownloadCaptionsWithTimecodes && !isProcessing && !isCallUnprocessed &&
			<IconMenuItem
				title={'Captions with timecodes'}
				onClick={onDownloadCaptionsWithTimecodes}
			>
				<TextCodesIcon />
			</IconMenuItem>
			}
			{!!bookId && !!onOpenBook
				? <IconMenuItem
					title={'Open book'}
					onClick={handleOpenBook}
				>
					<EndlessBookIcon />
				</IconMenuItem>
				: !!onCreateBook && !isProcessing && !isCallUnprocessed &&
				<IconMenuItem
					title={'Create new book'}
					onClick={handleCreateBook}
				>
					<EndlessBookIcon />
				</IconMenuItem>
			}

			<Divider />

			{!!onDeleteVideo && hasVideo && !isProcessing &&
				<IconMenuItem
					title={canChooseVideoAndAudio ? 'Remove video/audio' : 'Remove video'}
					withChevron
					onClick={handleConfirmDeleteVideo}
				>
					<CloseIcon />
				</IconMenuItem>
			}
			{/* Deleting the standalone audio file changes nothing once a video exists (its sound is
				already muxed in), so this only makes sense once there's audio with no video left. */}
			{!!onDeleteAudio && hasAudio && !hasVideo && !isProcessing &&
				<IconMenuItem
					title={'Remove audio'}
					withChevron
					onClick={handleConfirmDeleteAudio}
				>
					<CloseIcon />
				</IconMenuItem>
			}
			{!!onDelete &&
				<IconMenuItem
					title={'Delete recording'}
					withChevron
					onClick={handleConfirmDelete}
				>
					<TrashIcon
						sx={{
							width: theme.spacing(2) + ' !important',
							height: theme.spacing(2) + ' !important',
						}}
					/>
				</IconMenuItem>
			}
			{!!canEdit && !!onEdit &&
			<IconMenuItem
				title={'Edit'}
				onClick={onEdit}
			>
				<PencilIcon />
			</IconMenuItem>
			}
			</>
		</ButtonMenu>

		<ConfirmPopup
			anchor={pendingAction ? menuBtnRef.current : null}
			text={pendingAction ? confirmTextByAction[pendingAction].text : ''}
			description={pendingAction ? confirmTextByAction[pendingAction].description : ''}
			confirmTitle={pendingAction === 'delete' ? 'Delete' : 'Remove'}
			closeTitle={'Cancel'}
			// deleteVideo: only show the choice when there's a standalone audio file to keep or drop too.
			options={pendingAction === 'deleteVideo' && canChooseVideoAndAudio ? DELETE_VIDEO_OPTIONS : undefined}
			defaultOption={'video'}
			// delete: only offer the escalation where a "for everyone" action actually exists.
			checkboxLabel={pendingAction === 'delete' && onDeleteForEveryone ? 'Delete for everyone' : undefined}
			onConfirm={handleConfirmedAction}
			onClose={() => setPendingAction(null)}
			popoverProps={{
				className: classControl,
				disablePortal: true,
				// where the menu it replaces was: down-right from the button
				anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
				transformOrigin: { vertical: 'top', horizontal: 'left' },
			}}
		/>
	</>;

	const iconSx: SxProps = {
		width: theme.spacing(5),
		height: theme.spacing(5),
		color: theme.palette.grey[400],
	}

	const img = isAudio
		? <Stack
			className={`${className}__img`}
			justifyContent={'center'}
			alignItems={'center'}
			sx={{
				position: 'relative',
				aspectRatio: 16/9,
				background: theme.palette.grey[50],
				borderRadius: theme.spacing(1),
				overflow: 'hidden',

				[`.${className}.big &`]: {
					minWidth: theme.spacing(16),
					maxWidth: theme.spacing(16),
				},

				// recordings: a 56px circle with the participants' faces, the tile shows the duration in its info
				...(isRecording && {
					flexShrink: 0,
					aspectRatio: 1,
					borderRadius: '50%',
					width: theme.spacing(7),
					[`.${className}.big &`]: {
						minWidth: theme.spacing(7),
						maxWidth: theme.spacing(7),
					},
				}),

				...(isProcessing && {
					backgroundImage: `linear-gradient(90deg, ${theme.palette.grey[50]} 25%, ${theme.palette.grey[100]} 50%, ${theme.palette.grey[50]} 75%)`,
					backgroundSize: '200% 100%',
					animation: 'animation_recording_process 1.5s ease-in-out infinite',
					'@keyframes animation_recording_process': {
						'0%': { backgroundPosition: '200% 0' },
						'100%': { backgroundPosition: '-200% 0' },
					},
				}),
			}}
			onClick={handeClick}
		>
			{isProcessing && isRecording
				// no room for the progress line in the circle: a spinner, the line goes under "Processing..."
				? <CircularProgress
					size={theme.spacing(2.5)}
					onClick={(e) => {
						e.stopPropagation();
						openLog();
					}}
				/>
			: isProcessing
				? <Stack
					alignItems={'center'}
					gap={.5}
					sx={{
						p: theme.spacing(1),
						fontSize: theme.typography.subtitle1.fontSize,
						color: theme.palette.grey[500],
						textAlign: 'center',
					}}
					onClick={(e) => {
						e.stopPropagation();
						openLog();
					}}
				>
					{/* Only the live run's own last line - isProcessing can also be true for reasons
					unrelated to progressLog (saveLevel change, manual Delete audio/video), where
					progressLog may still hold a stale "Done"/"Failed" from an earlier finished run. */}
					{!!progressLog.length && isLogActive
						? lastMessage
						: 'Please wait…'
					}
				</Stack>
				: isRecording
					? recordingThumb || <SchoolIcon
						sx={iconSx}
					/>
					: isText
						? <TextIcon
							sx={iconSx}
						/>
						: <AudioIcon
							sx={iconSx}
						/>
			}

			{levelLabel}
			{percent}

			{!!durationFormatted && !isRecording &&
				<Stack
					sx={{
						position: 'absolute',
						right: theme.spacing(1),
						bottom: theme.spacing(1),
						p: theme.spacing(.1, .5),
						color: theme.palette.grey[700],
						fontSize: '11px',
						borderRadius: theme.spacing(.25),
						background: alpha(theme.palette.common.white, .9),
					}}
				>
					{durationFormatted}
				</Stack>
			}
		</Stack>
		: <Box
			className={`${className}__img`}
			sx={{
				position: 'relative',
				aspectRatio: 16/9,
				backgroundPosition: '50% 50%',
				backgroundSize: 'cover',
				backgroundRepeat: 'no-repeat',
				backgroundImage: `url(${thumbUrl})`,
				borderRadius: theme.spacing(1),
				overflow: 'hidden',

				[`.${className}.big &`]: {
					minWidth: theme.spacing(16),
				},
			}}
			onClick={handeClick}
		>
			{levelLabel}
			{percent}
		</Box>;

	const text = <Stack
		className={`${className}__text`}
		gap={.5}
		sx={{
			[`.${className}.big &`]: {
				minWidth: 0,
				pr: theme.spacing(4),
			},
		}}
		onClick={handeClick}
	>
		{titleTooltip
			? <Tooltip
				enterDelay={500}
				title={titleTooltip}
				placement={'top'}
				disableInteractive
				slotProps={LARGE_TOOLTIP_SLOT_PROPS}
			>
				{titleElement}
			</Tooltip>
			: isLongTitle
			? <Tooltip
				enterDelay={1000}
				title={title}
				placement={'top'}
				disableInteractive
			>
				{titleElement}
			</Tooltip>
			: titleElement
		}

		{isProcessing ?
			<>
				<Text
					className={`${className}__title-loading`}
					lineHeight={`1.3 !important`}
					sx={{
						fontSize: theme.typography.subtitle2,
						fontWeight: 600,
					}}
				>
					Processing...
				</Text>
				{/* recordings: the step that used to sit in the thumbnail, no room for it in the circle */}
				{isRecording &&
					<Box
						sx={{
							fontSize: theme.typography.subtitle1.fontSize,
							color: theme.palette.grey[500],
							cursor: 'pointer',
						}}
						onClick={(e) => {
							e.stopPropagation();
							openLog();
						}}
					>
						{!!progressLog.length && isLogActive
							? lastMessage
							: 'Please wait…'
						}
					</Box>
				}
			</>
			: isCallUnprocessed &&
				<Text
					className={`${className}__title-loading`}
					lineHeight={`1.3 !important`}
					sx={{
						fontSize: theme.typography.subtitle2,
						fontWeight: 600,
					}}
				>
					Process failed
				</Text>
		}

		<Stack
			flexDirection={'row'}
			flexWrap={'wrap'}
			columnGap={2}
		>
			{info}

			{!!channel &&
				<Tooltip
					title={`Channel: ${channel}`}
					enterDelay={500}
					placement={'top'}
					disableInteractive
				>
					<Text
						className={classControl}
						sx={{
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							fontSize: theme.typography.subtitle1,
							fontWeight: 600,
							color: theme.palette.grey[400],
							whiteSpace: 'nowrap',
						}}
						onClick={handleMenuOpen}
					>
						{channel}
					</Text>
				</Tooltip>
			}

			{!isBig && !!groupTitle &&
				<Tooltip
					title={`Playlist: ${groupTitle}`}
					enterDelay={500}
					placement={'top'}
					disableInteractive
				>
					<Text
						sx={{
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							fontSize: theme.typography.subtitle1,
							fontWeight: 300,
							color: theme.palette.grey[500],
							whiteSpace: 'nowrap',
						}}
					>
						{groupTitle}
					</Text>
				</Tooltip>
			}

		</Stack>

		{isCall && !isProcessing &&
			<Box
				sx={{
					color: theme.palette.grey[500],
					fontSize: theme.typography.subtitle1,
				}}
			>
				Unprocessed call
			</Box>
		}

	</Stack>;

	const ref = React.useRef(null);
	React.useEffect(() => {
		let timer: any;
		if (isActive || logOpen) {
			timer = setTimeout(() => {
				if (ref.current) {
					let el = ref.current as HTMLElement;
					el.scrollIntoView({ block: 'center', behavior: 'smooth' });
				}
			}, 300);
		}
		return () => {
			if (timer) clearTimeout(timer);
		}
	}, [isActive, logOpen]);

	if (locked) {
		return <Stack
				justifyContent={'center'}
				alignItems={'center'}
		>
			<CircularProgress />
		</Stack>
	}

	return (
		<Stack
			className={`${className} ${isBig ? 'big' : ''} ${isSaved ? 'saved' : ''} ${(isActive || logOpen) ? 'active' : ''}
				${(isProcessing || isCallUnprocessed) ? 'loading' : ''}
				${isCall ? 'call' : ''}`}
			sx={{
				position: 'relative',
				flex: `1 1 100%`,
				minWidth: 0,
				p: theme.spacing(1),
				borderRadius: theme.spacing(1),
				cursor: 'pointer',
				transition: 'background .3s ease',

				'&:hover': {
					background: theme.palette.grey[50],
				},

				[`&:hover .${className}__menu`]: {
					transform: 'scale(1)',
					pointerEvents: 'all',
				},

				'&.big': {
					flexDirection: 'row',
					alignItems: 'flex-start',
					gap: 2,
					pb: 2,
				},

				'&.saved > *': {
					opacity: .3,
					transition: 'opacity .3s ease',
				},

				'&.saved:hover > *': {
					opacity: 1,
				},

				'&.active': {
					background: theme.palette.primary.light,
				},

				'&.loading': {
					cursor: 'wait',

					[`.${className}__text`]: {
						opacity: .5,
					},

					[`.${className}__title`]: {
						display: 'none',
					},
				},

				'&.loading:hover': {
					[`.${className}__text`]: {
						opacity: 1,
					},

					[`.${className}__title`]: {
						display: 'block',
					},

					[`.${className}__title-loading`]: {
						display: 'none',
					},
				},

				'&.call': {
					[`.${className}__text`]: {
						opacity: .5,
					},
				},

				// touch: the menu button is always shown, at the tile's corner (not the thumbnail's)
				...(isMobileLayout && {
					[`.${className}__menu`]: {
						transform: 'scale(1)',
						pointerEvents: 'all',
						top: `${theme.spacing(.5)} !important`,
						right: `${theme.spacing(.5)} !important`,
						boxShadow: 'none',
					},

					// recordings keep their 56px circle
					...(!isRecording && {
						[`&.big .${className}__img`]: {
							minWidth: `${theme.spacing(10.5)} !important`,
							maxWidth: `${theme.spacing(10.5)} !important`,
						},
					}),

					// the menu button only overlaps the title, so the info rows get the full width
					[`&.big .${className}__text`]: {
						pr: 0,
					},
					[`&.big .${className}__title`]: {
						pr: 5,
					},
				}),
			}}
			gap={1}
			ref={ref}
		>
			<Box
				sx={{
					position: 'relative',
				}}
			>
				{img}
				{!isMobileLayout && menu}
			</Box>
			{text}
			{isMobileLayout && menu}

			{isSaved &&
				<Stack
					gap={.5}
					flexDirection={'row'}
					alignItems={'center'}
					sx={{
						position: 'absolute',
						left: theme.spacing(.5),
						top: theme.spacing(.5),
						width: 'auto',
						height: theme.spacing(2.5),
						px: theme.spacing(1),
						fontSize: '9px',
						letterSpacing: '0.6px',
						color: theme.palette.grey[700],
						textTransform: 'uppercase',
						background: theme.palette.common.white,
						borderRadius: theme.spacing(1.5),

						[`.${className}.saved &`]: {
							opacity: 1,
						},
					}}
				>
					<LibraryIcon
						sx={{
							width: theme.spacing(2),
							height: theme.spacing(2),
							color: theme.palette.primary.main,
						}}
					/>
					in my content
			</Stack>}

			{(!!progressLog.length || reprocessSettingsOpen) &&
				<CallRecordingProgressModalHOC
					open={logOpen}
					onClose={handleLogClose}
					title={modalTitle || title}
					progressLog={progressLog}
					isProcessing={isLogActive}
					startedAt={startedAt}
					stepStartedAt={stepStartedAt}
					updatedAt={updatedAt}
					audioId={audioId}
					settingsSection={settingsSection || undefined}
					onProcess={onReprocess}
					onCancel={onCancel}
				/>
			}
		</Stack>

	)
};
